import { saveAs } from "file-saver";
import { utils, write } from "xlsx";
import { Box, IconButton, Typography } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Primary } from "../../constants/Spalvos";
const ExcelJS = require("exceljs");

export const exportToExcelWithMultiRowHeader = async ({ data, fileName, multiHeaderConfig }) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Sheet1");

  const singleHeader = multiHeaderConfig.singleHeader;
  const nestedHeader = multiHeaderConfig.nestedHeader;

  const row1 = worksheet.getRow(1);
  const row2 = worksheet.getRow(2);
  const row3 = worksheet.getRow(3);

  let colIndex = 1;

  // Row 1: Single header columns (merged over 3 rows)
  singleHeader.forEach((col) => {
    worksheet.mergeCells(1, colIndex, 3, colIndex);
    const cell = row1.getCell(colIndex);
    cell.value = col.label;
    cell.alignment = { vertical: "middle", horizontal: "center" };
    cell.font = { bold: true };
    colIndex++;
  });

  // Row 1: Nested section titles
  nestedHeader.forEach((section) => {
    const sectionColumns = section.children.flatMap((child) =>
      child.children ? child.children : [child]
    );
    const span = sectionColumns.length;
    worksheet.mergeCells(1, colIndex, 1, colIndex + span - 1);
    const cell = row1.getCell(colIndex);
    cell.value = section.title;
    cell.alignment = { horizontal: "center" };
    cell.font = { bold: true };
    colIndex += span;
  });

  // Row 2: Group names or direct labels
  colIndex = singleHeader.length + 1;
  nestedHeader.forEach((section) => {
    section.children.forEach((child) => {
      if (child.children) {
        // Grouped
        const span = child.children.length;
        worksheet.mergeCells(2, colIndex, 2, colIndex + span - 1);
        const cell = row2.getCell(colIndex);
        cell.value = child.group;
        cell.alignment = { horizontal: "center" };
        cell.font = { bold: true };
        colIndex += span;
      } else {
        // Flat column under section
        worksheet.mergeCells(2, colIndex, 3, colIndex);
        const cell = row2.getCell(colIndex);
        cell.value = child.label;
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.font = { bold: true };
        colIndex++;
      }
    });
  });

  // Row 3: Labels of grouped columns only
  colIndex = singleHeader.length + 1;
  nestedHeader.forEach((section) => {
    section.children.forEach((child) => {
      if (child.children) {
        child.children.forEach((col) => {
          const cell = row3.getCell(colIndex + singleHeader.length);
          cell.value = col.label;
          cell.alignment = { horizontal: "center" };
          cell.font = { bold: true };
          colIndex++;
        });
      } else {
        // Already handled in row 2 for flat ones
        return;
      }
    });
  });

  worksheet.getRow(1).commit();
  worksheet.getRow(2).commit();
  worksheet.getRow(3).commit();

  // Define flat list of all column keys to map data correctly
  const allColumns = [
    ...singleHeader,
    ...nestedHeader.flatMap((section) =>
      section.children.flatMap((child) => (child.children ? child.children : [child]))
    ),
  ];

  worksheet.columns = allColumns.map((col) => ({
    key: col.dataKey,
    width: col.excelCellWidth || 12, // Use provided width or fallback to 12
  }));

  // Add rows of data
  data.forEach((rowData) => {
    const row = allColumns.map((col) => rowData[col.dataKey] ?? "");
    worksheet.addRow(row);
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, `${fileName || "export"}.xlsx`);
};

// Add this function after existing exportToExcelWithMultiRowHeader function
export const exportToExcelWithSimpleHeader = async ({ data, columns, fileName, groupByCow }) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Sheet1");

  // Define columns with proper headers and widths
  worksheet.columns = columns.map((col) => ({
    header: col.label,
    key: col.dataKey,
    width: col.excelCellWidth || Math.floor(col.width / 8) || 12,
  }));

  // Apply header styling
  const headerRow = worksheet.getRow(1);
  headerRow.eachCell((cell) => {
    cell.font = { bold: true };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: "none",
    };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  // Add data rows, skipping header rows
  const filteredData = data.filter((row) => !row.isHeader);

  if (groupByCow) {
    // Group data by cow
    const groupedData = new Map();

    filteredData.forEach((row) => {
      const cowId = row.karve;
      if (!groupedData.has(cowId)) {
        groupedData.set(cowId, []);
      }
      groupedData.get(cowId).push(row);
    });

    // Process each cow group
    groupedData.forEach((cowRows, cowId) => {
      cowRows.forEach((row, index) => {
        const rowValues = {};

        columns.forEach((col) => {
          // Only show cow info (karve, veisle, banda) on the first row of each cow
          if (
            (col.dataKey === "karve" || col.dataKey === "veisle" || col.dataKey === "banda") &&
            index > 0
          ) {
            rowValues[col.dataKey] = ""; // Empty for subsequent rows of the same cow
          } else {
            // Process special values like dates or formatted text
            if (col.dataKey === "bukle") {
              rowValues[col.dataKey] =
                row[col.dataKey] === "s"
                  ? "Sėklinta"
                  : row[col.dataKey] === "n"
                  ? "Nesėklinta"
                  : row[col.dataKey] === "u"
                  ? "Užtrūkusi"
                  : row[col.dataKey] === "v"
                  ? "Veršinga"
                  : "Nežinoma";
            } else if (col.dataKey === "kontroline_data" && row[col.dataKey]) {
              rowValues[col.dataKey] = row[col.dataKey].split("T")[0];
            } else {
              rowValues[col.dataKey] = row[col.dataKey];
            }
          }
        });

        worksheet.addRow(rowValues);
      });
    });
  } else {
    // Original logic for non-grouped data
    filteredData.forEach((row) => {
      const rowValues = {};

      columns.forEach((col) => {
        if (col.dataKey === "bukle") {
          rowValues[col.dataKey] =
            row[col.dataKey] === "s"
              ? "Sėklinta"
              : row[col.dataKey] === "n"
              ? "Nesėklinta"
              : row[col.dataKey] === "u"
              ? "Užtrūkusi"
              : row[col.dataKey] === "v"
              ? "Veršinga"
              : "Nežinoma";
        } else if (col.dataKey === "kontroline_data" && row[col.dataKey]) {
          rowValues[col.dataKey] = row[col.dataKey].split("T")[0];
        } else {
          rowValues[col.dataKey] = row[col.dataKey];
        }
      });

      worksheet.addRow(rowValues);
    });
  }

  // Auto-format column widths for better readability
  worksheet.columns.forEach((column) => {
    let maxLength = column.header?.length || 12;
    column.eachCell({ includeEmpty: true }, (cell) => {
      const cellLength = cell.value ? cell.value.toString().length : 10;
      if (cellLength > maxLength) {
        maxLength = cellLength;
      }
    });
    column.width = Math.min(maxLength + 2, 30); // Cap width at 30
  });

  // Create and save the Excel file
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, `${fileName || "export"}.xlsx`);
};

export const exportToExcel = ({ data, columns, widths, fileName }) => {
  const worksheet = utils.json_to_sheet(data);
  const workbook = utils.book_new();
  utils.book_append_sheet(workbook, worksheet, "Sheet1");
  utils.sheet_add_aoa(worksheet, [columns], { origin: "A1" });
  widths && (worksheet["!cols"] = widths.map((w) => ({ wch: w })));
  const excelBuffer = write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(blob, `${fileName}.xlsx`); //57
};

export const exportToExcelWithRules = ({ data, columns, fileName, formatRules, widths }) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Sheet1");

  // stulpeliai gali būti tušti, todėl juos pridedame jei nėra
  if (!columns || (columns.length === 0 && data.length > 0)) {
    columns = Object.keys(data[0] || {});
  }

  // sukuria stulpelisu su headeriu excelio faile
  worksheet.columns = columns.map((col, index) => ({
    header: col,
    key: col,
    width: widths ? widths[index] : 12, // Use provided width or default
  }));

  // prideda duomenis
  data.forEach((row) => {
    worksheet.addRow(row);
  });

  // formatavimo taisykles jei tokios yra
  if (formatRules && Array.isArray(formatRules)) {
    worksheet.eachRow((row, rowIndex) => {
      if (rowIndex === 1) return; // skip header

      formatRules.forEach((rule) => {
        const columnIndex = columns.indexOf(rule.column) + 1;
        if (columnIndex === 0) return;

        const cell = row.getCell(columnIndex);

        const controlColumnIndex = columns.indexOf(rule.controlColumn) + 1;
        if (controlColumnIndex === 0) return;

        const controlCell = row.getCell(controlColumnIndex);

        if (rule.condition(cell.value, controlCell.value)) {
          // pakeicia teksto jei to reikia
          if (rule.transformValue) {
            cell.value = rule.transformValue(cell.value);
          }

          // padaro pasvirusi nuraukima per laukeli
          cell.border = {
            ...(cell.border || {}),
            diagonal: {
              up: true,
              style: "thin",
            },
          };
        }
      });
    });
  }

  // sukuria bloba ir parsiuncia faila
  return workbook.xlsx
    .writeBuffer()
    .then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "export.xlsx";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    })
    .catch((err) => {
      console.error("Error creating Excel file:", err);
    });
};

/**
 * Komponentas, skirtas sugeneruoti xlsx faila is duoto duomenu masyvo
 * @param {[]} data duomenu masyvas
 * @param {[]} column stulpeliu pavadinimu masyvas
 * @param {[]} widths excelio stulpeliu plociu pavadinimas
 * @param {[]} fileName failo pavadinimas
 * @param {[]} align nuorodos lygivimas, default flex-start
 * @returns
 */
const ExportExcel = ({ data, columns, widths, fileName, align }) => {
  return (
    <Box display="flex" justifyContent={align ? align : "flex-start"}>
      <IconButton
        onClick={() =>
          exportToExcel({
            data: data,
            columns: columns,
            widths: widths,
            fileName: fileName,
          })
        }
        sx={{ color: Primary, padding: 0 }}
      >
        <Typography>Atsisiųsti duomenis</Typography>
        <FileDownloadIcon />
      </IconButton>
    </Box>
  );
};

export default ExportExcel;
